import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Section, Chip, arrayField, parseArray } from "@/growth/components/_ui";
import * as gc from "@/growth/api";

const F = ({ name, value, onChange, placeholder = "" }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">{name}</Label>
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={`gc-brand-${name.toLowerCase().replace(/\s+/g, "-")}`} />
  </div>
);

export default function BrandBrainTab({ wsId, onChanged }) {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [changeNote, setChangeNote] = useState("");

  const loadVersions = async () => {
    const r = await gc.listBrandVersions(wsId);
    setVersions(r.items || []);
    if (!data && r.items?.length) setData(r.items[0].brand_data);
  };

  useEffect(() => { loadVersions(); }, [wsId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doExtract = async () => {
    setExtracting(true);
    try {
      const r = await gc.extractBrand({ workspace_id: wsId });
      setData(r);
      toast.success("Brand drafted from your website. Review each field before saving.");
    } catch { toast.error("Extract failed"); }
    finally { setExtracting(false); }
  };

  const doSave = async (approveAfter = false) => {
    if (!data) return;
    setSaving(true);
    try {
      const r = await gc.saveBrand({ workspace_id: wsId, brand_data: data, change_note: changeNote });
      toast.success(`Draft v${r.version} saved.`);
      if (approveAfter) {
        await gc.approveBrand(r.brand_id);
        toast.success(`Approved v${r.version} — everything downstream now uses this profile.`);
      }
      setChangeNote("");
      await loadVersions();
      onChanged?.();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) {
    return (
      <Section title="Brand Brain" subtitle="The approved source of truth every AI agent must respect. Nothing gets published without your version stamp.">
        <Card className="p-8 border-2 border-dashed border-slate-300 rounded-2xl text-center">
          <div className="text-4xl mb-3">🧠</div>
          <div className="font-bold text-slate-900 text-lg">No brand profile yet</div>
          <div className="text-sm text-slate-600 mt-1 max-w-md mx-auto">We&apos;ll draft one from your website. Every extracted fact is marked <em>unverified</em> until you approve it.</div>
          <Button className="mt-5 bg-slate-900 hover:bg-slate-800 rounded-full" onClick={doExtract} disabled={extracting} data-testid="gc-brand-extract-btn">
            {extracting ? "Drafting…" : "Draft brand from my website"}
          </Button>
        </Card>
      </Section>
    );
  }

  const set = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  return (
    <div>
      <Section
        title="Brand Brain"
        subtitle="Every field below is unverified until you save & approve a version. Downstream agents will only use approved versions."
        right={
          <Button variant="outline" onClick={doExtract} disabled={extracting} className="rounded-full" data-testid="gc-brand-redraft-btn">
            {extracting ? "Drafting…" : "Re-draft from web"}
          </Button>
        }
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <F name="Legal Name" value={data.legal_name} onChange={(v) => set("legal_name", v)} />
          <F name="One Liner" value={data.one_liner} onChange={(v) => set("one_liner", v)} placeholder="What you do in <=140 chars" />
          <F name="Public Names" value={arrayField(data.public_names)} onChange={(v) => set("public_names", parseArray(v))} placeholder="Comma-separated" />
          <F name="Services" value={arrayField(data.services)} onChange={(v) => set("services", parseArray(v))} placeholder="Comma-separated" />
          <F name="Voice Tone" value={data.voice_tone} onChange={(v) => set("voice_tone", v)} placeholder="e.g. Warm, expert, no jargon" />
          <F name="Primary CTA" value={data.primary_cta} onChange={(v) => set("primary_cta", v)} placeholder="Get in touch" />
          <F name="Vocabulary DO" value={arrayField(data.vocabulary_do)} onChange={(v) => set("vocabulary_do", parseArray(v))} placeholder="Words we like" />
          <F name="Vocabulary DON'T" value={arrayField(data.vocabulary_dont)} onChange={(v) => set("vocabulary_dont", parseArray(v))} placeholder="Words to avoid" />
          <F name="Differentiators" value={arrayField(data.differentiators)} onChange={(v) => set("differentiators", parseArray(v))} placeholder="Comma-separated" />
          <F name="Approved Claims" value={arrayField(data.approved_claims)} onChange={(v) => set("approved_claims", parseArray(v))} placeholder="Only claims we can prove" />
          <F name="Color Hints" value={arrayField(data.color_hints)} onChange={(v) => set("color_hints", parseArray(v))} placeholder="#0f172a, #10b981" />
          <F name="Languages" value={arrayField(data.languages)} onChange={(v) => set("languages", parseArray(v))} placeholder="en, es" />
        </div>

        <div className="mt-6">
          <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Change Note (for the version history)</Label>
          <Textarea rows={2} value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder="What changed in this revision?" className="mt-1" data-testid="gc-brand-change-note" />
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => doSave(false)} disabled={saving} className="rounded-full" data-testid="gc-brand-save-draft">
              {saving ? "Saving…" : "Save as draft"}
            </Button>
            <Button onClick={() => doSave(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 rounded-full" data-testid="gc-brand-save-approve">
              {saving ? "Saving…" : "Save & Approve version"}
            </Button>
          </div>
        </div>
      </Section>

      <Section title="Version History" subtitle="Every save is immutable. Restore by loading a version's values into the form and saving again.">
        {versions.length === 0 ? (
          <p className="text-sm text-slate-500">No versions yet.</p>
        ) : (
          <div className="space-y-3">
            {versions.map((v) => (
              <Card key={v.brand_id} className="p-4 border border-slate-200 rounded-xl" data-testid={`gc-brand-version-${v.version}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="font-bold text-slate-900">v{v.version} {v.change_note ? <span className="font-normal text-slate-600">· {v.change_note}</span> : null}</div>
                    <div className="text-xs text-slate-500">Created {new Date(v.created_at).toLocaleString()} {v.approved_at ? `· Approved ${new Date(v.approved_at).toLocaleString()}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip tone={v.status === "approved" ? "emerald" : "amber"}>{v.status}</Chip>
                    <Button size="sm" variant="outline" onClick={() => setData(v.brand_data)}>Load into form</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
